import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useLanguage } from '@/context/LanguageContext';
import { cn } from '@/lib/utils';

export const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();
    const { t } = useLanguage();

    const navLinks = [
        { name: t('nav.home'), path: '/' },
        { name: t('nav.about'), path: '/about' },
        { name: t('nav.committees'), path: '/committees' },
        { name: t('nav.venue'), path: '/venue' },
        { name: t('nav.contact'), path: '/contact' },
    ];

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location]);

    const isHome = location.pathname === '/';
    const navBackground = isScrolled || !isHome
        ? 'bg-background/95 backdrop-blur-nav shadow-elegant border-b border-border'
        : 'bg-transparent';

    const textColor = isScrolled || !isHome
        ? 'text-foreground'
        : 'text-primary-foreground';

    return (
        <nav className={cn(
            'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
            navBackground
        )}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3 group mt-10">
                        <img 
                            src="https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/d7t95s72_Ads%C4%B1z%20tasar%C4%B1m.png"
                            alt="Girne MUN Logo"
                            className="h-[280px] w-auto transition-all duration-300 ease-out drop-shadow-[0_4px_16px_rgba(201,162,39,0.35)] group-hover:scale-105 group-hover:drop-shadow-[0_8px_24px_rgba(201,162,39,0.55)]"
                        />
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-1">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={cn(
                                    'nav-link px-4 py-2 text-sm font-medium rounded-md transition-colors',
                                    textColor,
                                    location.pathname === link.path && 'active'
                                )}
                            >
                                {link.name}
                            </Link>
                        ))}
                    </div>

                    {/* CTA Button & Language Switcher */}
                    <div className="hidden lg:flex items-center gap-3">
                        <LanguageSwitcher variant={isScrolled || !isHome ? 'default' : 'light'} />
                        <Link to="/apply">
                            <Button
                                className={cn(
                                    "btn-premium px-6 py-2 text-sm font-semibold rounded-md",
                                    "text-primary hover:text-primary"
                                )}
                            >
                                <span>{t('nav.applyNow')}</span>
                            </Button>
                        </Link>
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="lg:hidden flex items-center gap-2">
                        <LanguageSwitcher variant={isScrolled || !isHome ? 'default' : 'light'} />
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className={cn("p-2 rounded-md", textColor)}
                            aria-label="Toggle menu"
                        >
                            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            <div className={cn(
                'lg:hidden absolute top-full left-0 right-0 bg-background border-b border-border shadow-elegant-lg',
                'transition-all duration-300 overflow-hidden',
                isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            )}>
                <div className="px-4 py-4 space-y-1">
                    {navLinks.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            className={cn(
                                'block px-4 py-3 text-foreground font-medium rounded-md transition-colors',
                                'hover:bg-secondary',
                                location.pathname === link.path && 'bg-secondary text-accent'
                            )}
                        >
                            {link.name}
                        </Link>
                    ))}
                    <div className="pt-4 border-t border-border">
                        <Link to="/apply" className="block">
                            <Button className="w-full btn-premium text-primary font-semibold">
                                <span>{t('nav.applyNow')}</span>
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
